import React, { useState, useEffect, useCallback } from 'react';
import { Card, Table, Button, Badge, Modal, LoadingSkeleton } from './ui';
import { PlusIcon } from '@heroicons/react/24/outline';
import { safeErrorMessage } from '../utils/apiError';

function PaymentPlans({ token }) {
  const [students, setStudents] = useState([]);
  const [plans, setPlans] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [formData, setFormData] = useState({
    student_id: '', total_amount: '', currency: 'USD', installment_amount: '',
    number_of_installments: '', start_date: ''
  });

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const studentsRes = await fetch('/api/v1/students', { headers: { 'Authorization': `Bearer ${token}` } });
      if (!studentsRes.ok) throw new Error('Failed to fetch students');
      const studentsData = await studentsRes.json();
      setStudents(studentsData);

      const allPlans = [];
      for (const student of studentsData) {
        try {
          const plansRes = await fetch(`/api/v1/students/${student.id}/payment-plans`, {
            headers: { 'Authorization': `Bearer ${token}` }
          });
          if (plansRes.ok) allPlans.push(...(await plansRes.json()));
        } catch (err) { /* skip */ }
      }
      setPlans(allPlans);
    } catch (error) {
      setError('Failed to load payment plans.');
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (parseFloat(formData.total_amount) <= 0) { setError('Total amount must be > 0'); return; }
    if (parseFloat(formData.installment_amount) <= 0) { setError('Installment amount must be > 0'); return; }
    if (parseInt(formData.number_of_installments) <= 0) { setError('Number of installments must be > 0'); return; }
    if (!formData.start_date) { setError('Start date is required'); return; }

    try {
      const response = await fetch('/api/v1/payment-plans', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({
          student_id: parseInt(formData.student_id, 10),
          total_amount: parseFloat(formData.total_amount),
          currency: formData.currency,
          installment_amount: parseFloat(formData.installment_amount),
          number_of_installments: parseInt(formData.number_of_installments, 10),
          start_date: formData.start_date
        })
      });

      if (response.ok) {
        setShowForm(false);
        setFormData({ student_id: '', total_amount: '', currency: 'USD', installment_amount: '', number_of_installments: '', start_date: '' });
        await fetchData();
      } else {
        setError(await safeErrorMessage(response, 'Failed to create payment plan'));
      }
    } catch (error) {
      setError('Network error. Please check your connection and try again.');
    }
  };

  const statusBadge = (status) => {
    const map = { active: 'success', completed: 'info', defaulted: 'danger' };
    return <Badge variant={map[status] || 'warning'}>{status || 'unknown'}</Badge>;
  };

  const formatDate = (dateString) => {
    if (!dateString) return '—';
    try { return new Date(dateString).toLocaleDateString(); } catch { return '—'; }
  };

  const columns = [
    { header: 'Student', render: (row) => row.student_name || `ID: ${row.student_id}` },
    { header: 'Total', render: (row) => `${typeof row.total_amount === 'number' ? row.total_amount.toFixed(2) : row.total_amount} ${row.currency}` },
    { header: 'Installment', render: (row) => `${typeof row.installment_amount === 'number' ? row.installment_amount.toFixed(2) : row.installment_amount} ${row.currency}` },
    { header: '# Payments', accessor: 'number_of_installments' },
    { header: 'Start', render: (row) => formatDate(row.start_date) },
    { header: 'End', render: (row) => formatDate(row.end_date) },
    { header: 'Next', render: (row) => formatDate(row.next_payment_date) },
    { header: 'Status', render: (row) => statusBadge(row.status) },
  ];

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem', flexWrap: 'wrap', gap: '0.75rem' }}>
        <h2 style={{ fontSize: '1.25rem', fontWeight: 700, color: 'var(--color-text)' }}>Payment Plans</h2>
        <Button variant="primary" size="sm" onClick={() => { setShowForm(!showForm); setError(''); }}>
          <PlusIcon style={{ width: 16, height: 16 }} />
          {showForm ? 'Cancel' : 'Create Plan'}
        </Button>
      </div>

      {error && <div className="alert alert-error">{error}</div>}

      <Modal isOpen={showForm} onClose={() => setShowForm(false)} title="Create Payment Plan">
        <form onSubmit={handleSubmit}>
          <div className="form-row">
            <div className="form-group">
              <label>Student *</label>
              <select value={formData.student_id} onChange={(e) => setFormData({...formData, student_id: e.target.value})} required>
                <option value="">Select Student</option>
                {students.map(s => <option key={s.id} value={s.id}>{s.full_name}</option>)}
              </select>
            </div>
            <div className="form-group">
              <label>Total Amount *</label>
              <input type="number" step="0.01" min="0" value={formData.total_amount} onChange={(e) => setFormData({...formData, total_amount: e.target.value})} required />
            </div>
          </div>
          <div className="form-row">
            <div className="form-group">
              <label>Currency *</label>
              <select value={formData.currency} onChange={(e) => setFormData({...formData, currency: e.target.value})}>
                <option value="USD">USD</option>
                <option value="ZWG">ZWG</option>
              </select>
            </div>
            <div className="form-group">
              <label>Installment Amount *</label>
              <input type="number" step="0.01" min="0" value={formData.installment_amount} onChange={(e) => setFormData({...formData, installment_amount: e.target.value})} required />
            </div>
          </div>
          <div className="form-row">
            <div className="form-group">
              <label>Number of Installments *</label>
              <input type="number" min="1" max="24" value={formData.number_of_installments} onChange={(e) => setFormData({...formData, number_of_installments: e.target.value})} required />
            </div>
            <div className="form-group">
              <label>Start Date *</label>
              <input type="date" value={formData.start_date} onChange={(e) => setFormData({...formData, start_date: e.target.value})} required />
            </div>
          </div>
          <div style={{ display: 'flex', gap: '0.5rem', marginTop: '1rem' }}>
            <Button type="submit" variant="primary">Create Plan</Button>
            <Button type="button" variant="secondary" onClick={() => setShowForm(false)}>Cancel</Button>
          </div>
        </form>
      </Modal>

      <Card title="Active Payment Plans">
        {loading ? (
          <LoadingSkeleton type="text" rows={5} />
        ) : (
          <Table columns={columns} data={plans} emptyMessage="No payment plans created yet." />
        )}
      </Card>
    </div>
  );
}

export default PaymentPlans;
